import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../contexts/AdminContext';
import {
  Users, Search, Filter, MoreVertical, Eye, Edit, Ban,
  ChevronLeft, ChevronRight, Coins, Video, Mail, Phone, Calendar
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api/admin';

export default function AdminUsersPage() {
  const { token, hasPermission } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [balanceAction, setBalanceAction] = useState({ amount: '', action: 'add', reason: '' });

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, search]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: pagination.page, limit: 15, search }
      });
      setUsers(res.data.users || []);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchUsers();
  };

  const viewUser = async (userId) => {
    try {
      const res = await axios.get(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUser(res.data);
      setShowUserModal(true);
    } catch (err) {
      toast.error('Failed to load user details');
    }
  };

  const handleAdjustBalance = async () => {
    if (!balanceAction.amount || isNaN(parseFloat(balanceAction.amount))) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      await axios.put(`${API}/users/${selectedUser.user.id}/balance`, {
        amount: parseFloat(balanceAction.amount),
        action: balanceAction.action,
        reason: balanceAction.reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Balance updated successfully');
      setShowBalanceModal(false);
      setBalanceAction({ amount: '', action: 'add', reason: '' });
      fetchUsers();
      viewUser(selectedUser.user.id); // Refresh user details
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update balance');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <Toaster position="top-center" theme="dark" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>Users</h1>
          <p className="text-zinc-500 text-sm mt-1">Manage platform users</p>
        </div>
        
        {/* Search */}
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users..."
              className="w-64 pl-10 pr-4 py-2.5 bg-zinc-800/50 border border-zinc-700 rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:border-indigo-500"
              data-testid="users-search"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2.5 bg-indigo-500 text-white font-medium rounded-xl hover:bg-indigo-600 transition-all"
          >
            Search
          </button>
        </form>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-zinc-900/50 rounded-xl p-4 border border-zinc-800">
          <p className="text-zinc-500 text-sm">Total Users</p>
          <p className="text-2xl font-bold text-white mt-1">{pagination.total}</p>
        </div>
      </div>

      {/* Users table */}
      <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">User</th>
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Contact</th>
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Balance</th>
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Streams</th>
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Joined</th>
                <th className="text-right px-6 py-4 text-zinc-400 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/50">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={6} className="px-6 py-4">
                      <div className="h-12 bg-zinc-800 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : users.length > 0 ? (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-zinc-800/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center overflow-hidden">
                          {user.profile_picture && user.profile_picture !== 'camera_new.png' ? (
                            <img 
                              src={`https://dev.naofan.app/avatars/${user.profile_picture}`}
                              alt={user.first_name}
                              className="w-10 h-10 object-cover"
                            />
                          ) : (
                            <span className="text-white font-bold">
                              {user.first_name?.charAt(0) || 'U'}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.first_name} {user.last_name}</p>
                          <p className="text-zinc-500 text-sm">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {user.phone && user.phone !== 'none' && (
                        <span className="text-zinc-400 text-sm">{user.phone}</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-amber-400 font-medium">
                        <Coins className="w-4 h-4" />
                        {user.balance?.toFixed(2) || '0.00'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-zinc-400">{user.stream_count || 0}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-zinc-500 text-sm">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => viewUser(user.id)}
                          className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-all"
                          title="View details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {hasPermission('users.manage') && (
                          <button
                            onClick={() => {
                              viewUser(user.id);
                              setTimeout(() => setShowBalanceModal(true), 500);
                            }}
                            className="p-2 text-zinc-400 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-all"
                            title="Adjust balance"
                          >
                            <Coins className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-zinc-500">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-800">
            <p className="text-zinc-500 text-sm">
              Page {pagination.page} of {pagination.pages} ({pagination.total} users)
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page <= 1}
                className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.pages}
                className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User details modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-zinc-800">
            <div className="p-6 border-b border-zinc-800">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center overflow-hidden">
                    {selectedUser.user.profile_picture && selectedUser.user.profile_picture !== 'camera_new.png' ? (
                      <img 
                        src={`https://dev.naofan.app/avatars/${selectedUser.user.profile_picture}`}
                        alt={selectedUser.user.first_name}
                        className="w-16 h-16 object-cover"
                      />
                    ) : (
                      <span className="text-white text-2xl font-bold">
                        {selectedUser.user.first_name?.charAt(0) || 'U'}
                      </span>
                    )}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                      {selectedUser.user.first_name} {selectedUser.user.last_name}
                    </h2>
                    <p className="text-zinc-500">{selectedUser.user.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="text-zinc-500 hover:text-white"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* User info grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-zinc-800/30 rounded-xl p-4">
                  <p className="text-zinc-500 text-sm flex items-center gap-2">
                    <Coins className="w-4 h-4" /> Balance
                  </p>
                  <p className="text-2xl font-bold text-amber-400 mt-1">
                    {selectedUser.user.balance?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="bg-zinc-800/30 rounded-xl p-4">
                  <p className="text-zinc-500 text-sm flex items-center gap-2">
                    <Video className="w-4 h-4" /> Streams
                  </p>
                  <p className="text-2xl font-bold text-white mt-1">
                    {selectedUser.streams?.length || 0}
                  </p>
                </div>
              </div>

              {/* Contact info */}
              <div className="space-y-3">
                <h3 className="text-white font-medium">Contact Information</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 text-zinc-400">
                    <Mail className="w-4 h-4" />
                    {selectedUser.user.email}
                  </div>
                  {selectedUser.user.mobile_phone && selectedUser.user.mobile_phone !== 'none' && (
                    <div className="flex items-center gap-3 text-zinc-400">
                      <Phone className="w-4 h-4" />
                      {selectedUser.user.mobile_phone}
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-zinc-400">
                    <Calendar className="w-4 h-4" />
                    Joined {selectedUser.user.DateCreated ? new Date(selectedUser.user.DateCreated).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
              </div>

              {/* Recent streams */}
              {selectedUser.streams?.length > 0 && (
                <div>
                  <h3 className="text-white font-medium mb-3">Recent Streams</h3>
                  <div className="space-y-2">
                    {selectedUser.streams.slice(0, 5).map((stream) => (
                      <div key={stream.id} className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
                        <div>
                          <p className="text-white text-sm">{stream.title}</p>
                          <p className="text-zinc-500 text-xs">
                            {new Date(stream.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          stream.status === 'live' ? 'bg-red-500/20 text-red-400' :
                          stream.status === 'ended' ? 'bg-zinc-700 text-zinc-400' :
                          'bg-amber-500/20 text-amber-400'
                        }`}>
                          {stream.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              {hasPermission('users.manage') && (
                <div className="flex gap-3 pt-4 border-t border-zinc-800">
                  <button
                    onClick={() => setShowBalanceModal(true)}
                    className="flex-1 py-3 bg-amber-500/20 text-amber-400 font-medium rounded-xl hover:bg-amber-500/30 transition-all flex items-center justify-center gap-2"
                  >
                    <Coins className="w-4 h-4" />
                    Adjust Balance
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Balance adjustment modal */}
      {showBalanceModal && selectedUser && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl max-w-md w-full border border-zinc-800">
            <div className="p-6 border-b border-zinc-800">
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                Adjust Balance
              </h3>
              <p className="text-zinc-500 text-sm mt-1">
                Modify tokens for {selectedUser.user.first_name}
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Action</label>
                <select
                  value={balanceAction.action}
                  onChange={(e) => setBalanceAction(prev => ({ ...prev, action: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                >
                  <option value="add">Add tokens</option>
                  <option value="subtract">Subtract tokens</option>
                  <option value="set">Set balance</option>
                </select>
              </div>

              <div>
                <label className="block text-zinc-400 text-sm mb-2">Amount</label>
                <input
                  type="number"
                  value={balanceAction.amount}
                  onChange={(e) => setBalanceAction(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="Enter amount"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-zinc-400 text-sm mb-2">Reason (optional)</label>
                <input
                  type="text"
                  value={balanceAction.reason}
                  onChange={(e) => setBalanceAction(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Reason for adjustment"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowBalanceModal(false)}
                  className="flex-1 py-3 bg-zinc-800 text-white font-medium rounded-xl hover:bg-zinc-700 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdjustBalance}
                  className="flex-1 py-3 bg-indigo-500 text-white font-bold rounded-xl hover:bg-indigo-600 transition-all"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
